import { Camera, CheckCircle2, Loader2, ScanFace, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  LECTURAS_ESTABLES,
  abrirCamara,
  analizarFotograma,
  cerrarCamara,
  prepararMotor,
  redondearDescriptor,
  revisarSoporte,
} from '@/lib/biometria/rostro';

interface CamaraFacialDialogProps {
  open: boolean;
  onClose: () => void;
  titulo: string;
  descripcion?: string;
  /** Capturas que se piden: 1 para entrar, hasta 5 para registrar la cara. */
  capturas?: number;
  /** Se llama con las capturas conseguidas y la confianza media. */
  onCapturas: (descriptores: number[][], calidad: number) => void;
  /** El dueño del diálogo está llamando a la API. */
  ocupado?: boolean;
  /** Mensaje de error de la API (se muestra dentro del diálogo). */
  error?: string | null;
  /** Ofrece volver a capturar: el dueño del diálogo reinicia el componente. */
  onReintentar?: () => void;
  /** Etiqueta del botón de reintento. */
  textoReintentar?: string;
}

type Estado = 'iniciando' | 'encuadrando' | 'analizando' | 'error';

/**
 * Diálogo de captura de la cara con la cámara del equipo.
 *
 * La cara se analiza en el navegador y sólo se entrega el descriptor (un vector de números), nunca
 * una imagen: el servidor no almacena fotos. Mientras no se den las condiciones (una sola persona,
 * cerca, de frente, persona real) se indica qué corregir; cuando la lectura es estable unas cuantas
 * veces seguidas, la captura se hace sola.
 *
 * Conviene montarlo sólo al abrir (`{abierto ? <CamaraFacialDialog …/> : null}`): así cada apertura
 * arranca limpia y la cámara se apaga al cerrar.
 */
export function CamaraFacialDialog({
  open,
  onClose,
  titulo,
  descripcion,
  capturas = 1,
  onCapturas,
  ocupado = false,
  error = null,
  onReintentar,
  textoReintentar = 'Volver a intentarlo',
}: CamaraFacialDialogProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const estableRef = useRef(0);
  const enviadoRef = useRef(false);
  const descriptoresRef = useRef<number[][]>([]);
  const calidadRef = useRef<number[]>([]);
  const onCapturasRef = useRef(onCapturas);

  const [estado, setEstado] = useState<Estado>('iniciando');
  const [aviso, setAviso] = useState<string | null>('Preparando la cámara…');
  const [hechas, setHechas] = useState(0);
  const [listo, setListo] = useState(false);

  // El soporte se calcula al pintar y no cambia mientras el diálogo está abierto.
  const soporte = revisarSoporte();

  useEffect(() => {
    onCapturasRef.current = onCapturas;
  }, [onCapturas]);

  /** Entrega las capturas al dueño del diálogo (una sola vez por captura). */
  const entregarCapturas = useCallback(() => {
    if (enviadoRef.current || descriptoresRef.current.length === 0) return;

    enviadoRef.current = true;
    setEstado('analizando');

    const media =
      calidadRef.current.reduce((suma, valor) => suma + valor, 0) /
      Math.max(calidadRef.current.length, 1);

    onCapturasRef.current(descriptoresRef.current, Number(media.toFixed(4)));
  }, []);

  useEffect(() => {
    if (!open || !soporte.ok) return undefined;

    let cancelado = false;
    let temporizador: number | null = null;
    const video = videoRef.current;

    const bucle = async () => {
      if (cancelado || !video || enviadoRef.current) return;

      try {
        const encuadre = await analizarFotograma(video);

        if (cancelado) return;

        setAviso(encuadre.aviso);

        if (!encuadre.listo || !encuadre.rostro) {
          estableRef.current = 0;
          setListo(false);
        } else {
          estableRef.current += 1;
          setListo(true);

          // Se espera a que la lectura sea estable: así no se captura una mueca ni un giro.
          if (estableRef.current >= LECTURAS_ESTABLES) {
            estableRef.current = 0;
            descriptoresRef.current = [
              ...descriptoresRef.current,
              redondearDescriptor(encuadre.rostro.descriptor),
            ];
            calidadRef.current = [...calidadRef.current, encuadre.rostro.vivacidad];
            setHechas(descriptoresRef.current.length);
            setListo(false);

            if (descriptoresRef.current.length >= capturas) {
              entregarCapturas();
              return;
            }
          }
        }
      } catch {
        if (cancelado) return;
        setEstado('error');
        setAviso('No se pudo analizar la imagen de la cámara.');
        return;
      }

      temporizador = window.setTimeout(bucle, 250);
    };

    void (async () => {
      try {
        // Los modelos se descargan la primera vez y quedan en la caché del navegador.
        await prepararMotor();

        if (cancelado || !video) return;

        await abrirCamara(video);

        if (cancelado) return;

        setEstado('encuadrando');
        setAviso('Colócate frente a la cámara.');
        void bucle();
      } catch (fallo) {
        if (cancelado) return;
        setEstado('error');
        setAviso(fallo instanceof Error ? fallo.message : 'No se pudo abrir la cámara.');
      }
    })();

    return () => {
      cancelado = true;
      if (temporizador !== null) window.clearTimeout(temporizador);
      cerrarCamara(video);
    };
  }, [open, capturas, soporte.ok, entregarCapturas]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={titulo}
      description={descripcion}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>

          {onReintentar && (error || estado === 'error') ? (
            <Button variant="outline" onClick={onReintentar} disabled={ocupado}>
              {textoReintentar}
            </Button>
          ) : null}

          {capturas > 1 && hechas > 0 && !ocupado && !error ? (
            <Button leftIcon={<CheckCircle2 className="size-4" />} onClick={entregarCapturas}>
              Guardar {hechas} {hechas === 1 ? 'captura' : 'capturas'}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}

        {!soporte.ok ? (
          <Alert variant="warning" title="Cámara no disponible">
            {soporte.motivo}
          </Alert>
        ) : (
          <>
            <div className="relative overflow-hidden rounded-2xl bg-ink-950">
              <video
                ref={videoRef}
                className="aspect-3/4 w-full scale-x-[-1] object-cover sm:aspect-4/3"
                playsInline
                muted
              />

              {/* Guía ovalada: ayuda a colocarse sin tapar la cara. */}
              <div
                aria-hidden="true"
                className={
                  'pointer-events-none absolute inset-0 flex items-center justify-center transition-colors ' +
                  (listo ? 'text-emerald-400' : 'text-white/70')
                }
              >
                <div
                  className={
                    'h-2/3 w-1/2 rounded-[50%] border-4 border-current shadow-[0_0_0_9999px_rgba(2,6,23,0.35)] ' +
                    (listo ? 'border-solid' : 'border-dashed')
                  }
                />
              </div>

              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-ink-950/90 to-transparent px-4 pt-8 pb-3 text-center">
                <p className="flex items-center gap-2 text-sm font-medium text-white">
                  {estado === 'analizando' || ocupado ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {estado === 'analizando' || ocupado
                    ? 'Comprobando tu cara…'
                    : (aviso ?? 'Mantén la posición…')}
                </p>
              </div>

              {estado === 'iniciando' ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink-950/80 px-6 text-center text-white">
                  <Spinner className="size-6" />
                  <p className="text-sm">
                    Preparando la cámara y los modelos (la primera vez tarda unos segundos)…
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex items-start gap-2 text-xs text-ink-500">
              <ScanFace className="mt-px size-4 shrink-0" aria-hidden="true" />
              <p>
                Tu cara se analiza en este equipo y sólo se guarda un código numérico que la
                representa; la imagen no se envía ni se almacena.
              </p>
            </div>

            {capturas > 1 ? (
              <p className="flex items-start gap-2 text-xs font-medium text-ink-600">
                <Camera className="mt-px size-4 shrink-0" aria-hidden="true" />
                <span>
                  Capturas: {hechas} de {capturas}
                  {hechas > 0 && hechas < capturas
                    ? ' · cambia un poco la posición (gira levemente la cabeza) para que la cara quede mejor registrada.'
                    : ''}
                </span>
              </p>
            ) : null}
          </>
        )}

        {error && capturas === 1 ? (
          <p className="flex items-start gap-2 text-xs text-ink-500">
            <ShieldAlert className="mt-px size-4 shrink-0" aria-hidden="true" />
            También puedes ingresar con tu contraseña o tu código de operador.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
